import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FilterPopoverPanel } from '@/components/filters/filter-dropdown-shell'
import { FILTER_ADD_PILL_CLASS, FILTER_POPOVER_CONTENT_CLASS } from '@/components/filters/filter-styles'
import { SegmentedFilterChip } from '@/components/filters/segmented-filter-chip'
import { cn } from '@/lib/utils'

type AddFilterPillButtonProps = {
  label: string
  className?: string
} & ButtonHTMLAttributes<HTMLButtonElement>

/** Empty-state add affordance: borderless `+ Label` at chip scale. */
const AddFilterPillButton = forwardRef<HTMLButtonElement, AddFilterPillButtonProps>(
  function AddFilterPillButton({ label, className, ...buttonProps }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        className={cn(FILTER_ADD_PILL_CLASS, className)}
        aria-label={`Add ${label} filter`}
        {...buttonProps}
      >
        <span className="shrink-0" aria-hidden>
          +
        </span>
        <span className="truncate">{label}</span>
      </button>
    )
  },
)

export type FilterDimensionPillProps = {
  label: string
  displayValue: string
  isActive: boolean
  popoverContent: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  className?: string
}

/** Additive filter affordance: ghost `+ Skill` when empty, segmented `Skill · 2 selected` chip when active. */
export function FilterDimensionPill({
  label,
  displayValue,
  isActive,
  popoverContent,
  open = false,
  onOpenChange,
  className,
}: FilterDimensionPillProps) {
  const openEditor = () => onOpenChange?.(true)

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {isActive ? (
        <PopoverAnchor asChild>
          <SegmentedFilterChip
            className={className}
            label={label}
            value={displayValue}
            onLabelClick={openEditor}
            onValueClick={openEditor}
            labelAriaLabel={`${label}: ${displayValue}. Click to edit filter.`}
            valueAriaLabel={`${label}: ${displayValue}. Click to edit filter.`}
          />
        </PopoverAnchor>
      ) : (
        <PopoverTrigger asChild>
          <AddFilterPillButton label={label} className={className} aria-expanded={open} />
        </PopoverTrigger>
      )}
      <PopoverContent
        align="start"
        className={cn(
          'w-auto min-w-[16rem] max-w-[min(100vw-2rem,24rem)] p-0',
          FILTER_POPOVER_CONTENT_CLASS,
        )}
      >
        <FilterPopoverPanel>{popoverContent}</FilterPopoverPanel>
      </PopoverContent>
    </Popover>
  )
}
